import { useEffect, useState } from 'react'
import ModalComponent from '..'
import { terminalProviderMap } from '../../../constants'
import { deepCopy } from '../../../helpers'
import { useGetTerminalsQuery } from '../../../redux-toolkits/terminals/terminals.slice'
import {
  FetchTerminalQuery,
  SingleTerminal,
  TerminalProvider,
} from '../../../redux-toolkits/terminals/terminals.types'
import { FulfilmentCentre } from '../../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { Button } from '../../button'
import { Status } from '../../cards/statusTag'
import { Checkbox } from '../../inputs/Checkbox'
import { SearchInput } from '../../inputs/searchInput'
import { TableComponent } from '../../table'

type V = SingleTerminal

export const SelectTerminal = ({
  maxSelection,
  open,
  terminals,
  setTerminals,
  fulfilmentCenter,
  onlyActive = true,
  provider,
  showSearch = true,
}: {
  maxSelection?: number
  open?: boolean
  terminals: V[]
  setTerminals: (creators: V[]) => void
  fulfilmentCenter?: FulfilmentCentre
  onlyActive?: boolean
  provider?: TerminalProvider
  showSearch?: boolean
}) => {
  const [queryParam, setQueryParam] = useState<FetchTerminalQuery>({
    page: 1,
    limit: 50,
    search: undefined,
    fulfilmentCentreId: fulfilmentCenter?._id,
    provider: provider,
  })

  const { data, isFetching } = useGetTerminalsQuery(
    {
      ...queryParam,
      search: queryParam.search || undefined,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !open,
    },
  )

  return (
    <div
      className="
        flex flex-col gap-2
        cursor-pointer
        "
    >
      {showSearch ? (
        <div className="bg-white pb-2 !max-w-full sticky top-0">
          <SearchInput
            searchFunction={(search) => {
              setQueryParam({
                ...queryParam,
                search: search,
                page: 1,
              })
            }}
            placeholder="Search terminal"
            className="!max-w-full"
          />
        </div>
      ) : undefined}

      <div className="w-full flex-1 overflow-y-scroll">
        <TableComponent
          rows={(data?.data?.docs ?? [])
            .filter((t) => (onlyActive ? t.status === 'ACTIVE' : true))
            .map((terminal) => {
              return {
                id: terminal._id,
                content: [
                  <Checkbox
                    className="!gap-6 !w-full"
                    disabled={
                      maxSelection !== undefined &&
                      terminals.length >= maxSelection &&
                      !terminals.map((c) => c._id).includes(terminal._id)
                    }
                    checkStatus={
                      terminals.map((c) => c._id)?.includes(terminal._id) ??
                      false
                    }
                    handleChange={(checked) => {
                      if (checked) {
                        const terminalsCopy = deepCopy(terminals)

                        terminalsCopy.push(terminal)

                        setTerminals(terminalsCopy)
                      } else {
                        const terminalsCopy = deepCopy(terminals)

                        setTerminals(
                          terminalsCopy.filter((v) => terminal._id !== v._id),
                        )
                      }
                    }}
                    key={terminal._id}
                    text={
                      <div className="flex !w-full gap-3 items-center">
                        <p className="flex-1 text-base text-black font-normal">
                          {terminal.tag}
                        </p>

                        <Status
                          text={terminalProviderMap[terminal?.provider].text}
                          type={
                            terminalProviderMap[terminal?.provider].pillVariant
                          }
                          className="!border border-blue ml-auto"
                        />
                      </div>
                    }
                  />,
                ],
              }
            })}
          loading={isFetching}
          isEmpty={[0, undefined].includes(data?.data?.docs?.length)}
          pageLimit={Number(queryParam.limit)}
          onPageChange={(page) => {
            setQueryParam({
              ...queryParam,
              page: page,
            })
          }}
        />
      </div>
    </div>
  )
}

export const SelectPOSTerminalModal = ({
  open,
  setOpen,
  onCloseCallback,
  onSuccessCallback,
  selectedTerminals,
  maxSelection,
  loading = false,
  fulfilmentCenter,
  onlyActive = true,
}: {
  open: boolean
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: V[]) => void
  selectedTerminals?: V[]
  maxSelection?: number
  loading?: boolean
  fulfilmentCenter?: FulfilmentCentre
  onlyActive?: boolean
}) => {
  const [terminals, setTerminals] = useState<V[]>([])

  useEffect(() => {
    if (open) {
      setTerminals(selectedTerminals ?? [])
    }
  }, [open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback?.()
      }}
      title={`Select Terminal ${
        fulfilmentCenter?.name ? `(${fulfilmentCenter?.name})` : ''
      }`}
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-6">
        <SelectTerminal
          maxSelection={maxSelection}
          open={open}
          terminals={terminals}
          setTerminals={setTerminals}
          fulfilmentCenter={fulfilmentCenter}
          onlyActive={onlyActive}
        />

        <Button
          type="button"
          primary
          label="Continue"
          onClick={() => {
            onSuccessCallback?.(terminals)
          }}
          loading={loading}
          disabled={loading || terminals.length < 1}
          className="sticky bottom-0"
        />
      </div>
    </ModalComponent>
  )
}
